import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Layout } from "../components/Layout";
import { useAuth } from "../contexts/AuthContext";
import { useLanguage } from "../contexts/LanguageContext";
import {
	Card,
	CardContent,
	CardHeader,
	CardTitle,
} from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "../components/ui/select";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
} from "../components/ui/dialog";
import { Plus, Search, Zap, Edit2, Trash2, Timer } from "lucide-react";
import { toast } from "sonner";
import { ConditioningTemplateEditor } from "../components/ConditioningTemplateEditor";
import {
	emptyConditioningTemplate,
	normalizeConditioningRounds,
} from "../lib/coachUtils";
import { fetchAPI } from "../lib/utils";
import { formatIntervalSecondsLabel } from "../lib/conditioningResultUtils";

const CONDITIONING_TYPES = [
	{ value: "for_time", label: "For Time" },
	{ value: "amrap", label: "AMRAP" },
	{ value: "emom", label: "EMOM" },
	{ value: "tabata", label: "Tabata" },
	{ value: "interval", label: "Interval" },
	{ value: "hiit", label: "HIIT" },
	{ value: "custom", label: "Custom" },
];

const getDisplayEmomRounds = (template) => {
	const raw = template?.emom_rounds;
	if (raw === null || raw === undefined || raw === "") {
		return null;
	}
	const n = Number(raw);
	if (!Number.isFinite(n) || n < 1) {
		return null;
	}
	return Math.floor(n);
};

const getForTimeRoundsDisplay = (rounds) => {
	if (!rounds || rounds <= 0) {
		return "";
	}
	if (Number.isInteger(rounds)) {
		return rounds === 1 ? "1 round" : `${rounds} rounds`;
	}
	const normalized = normalizeConditioningRounds(rounds);
	return `${normalized} rounds`;
};

export default function ConditioningLibrary() {
	const { token, user } = useAuth();
	const { t } = useLanguage();

	const [loading, setLoading] = useState(true);
	const [templates, setTemplates] = useState([]);
	const [editorExercises, setEditorExercises] = useState([]);
	const [searchTerm, setSearchTerm] = useState("");
	const [typeFilter, setTypeFilter] = useState("all");
	const [scopeFilter, setScopeFilter] = useState("mine");
	const [adminOwners, setAdminOwners] = useState([]);
	const [selectedOwnerId, setSelectedOwnerId] = useState("all");

	const [showDialog, setShowDialog] = useState(false);
	const [editingTemplate, setEditingTemplate] = useState(null);
	const [formData, setFormData] = useState({
		...emptyConditioningTemplate(),
		library_scope: "personal",
	});
	const [saving, setSaving] = useState(false);

	const canManageGlobal = user?.role === "admin";
	const currentUserId = String(user?.user_id || "");
	const normalizeScope = (value) => {
		const text = String(value || "")
			.trim()
			.toLowerCase();
		return text === "personal" ? "personal" : "global";
	};
	const ownerFor = (item) => String(item?.owner_id || item?.coach_id || "");
	const applyClientScopeFilter = (items, scope, ownerId = "all") => {
		const list = Array.isArray(items) ? items : [];
		const filteredByOwner =
			canManageGlobal && scope === "all" && ownerId !== "all"
				? list.filter(
						(item) =>
							ownerFor(item) === ownerId &&
							normalizeScope(item?.library_scope) === "personal",
					)
				: list;
		if (scope === "global") {
			return filteredByOwner.filter(
				(item) => normalizeScope(item?.library_scope) === "global",
			);
		}
		if (scope === "mine") {
			return filteredByOwner.filter(
				(item) =>
					normalizeScope(item?.library_scope) === "personal" &&
					ownerFor(item) === currentUserId,
			);
		}
		if (scope === "visible") {
			return filteredByOwner.filter((item) => {
				const tag = normalizeScope(item?.library_scope);
				return tag === "global" || ownerFor(item) === currentUserId;
			});
		}
		if (scope === "all") {
			return filteredByOwner;
		}
		return filteredByOwner;
	};
	const getConditioningTypeBadgeClass = (type) => {
		const normalized = String(type || "")
			.trim()
			.toLowerCase();
		const colors = {
			for_time: "bg-orange-500/20 text-orange-300",
			amrap: "bg-lime-500/20 text-lime-300",
			emom: "bg-cyan-500/20 text-cyan-300",
			tabata: "bg-rose-500/20 text-rose-300",
			interval: "bg-blue-500/20 text-blue-300",
			hiit: "bg-fuchsia-500/20 text-fuchsia-300",
			custom: "bg-zinc-500/20 text-zinc-300",
		};
		return colors[normalized] || "bg-volt/15 text-volt";
	};
	const getScopeBadgeClass = (scope) =>
		normalizeScope(scope) === "global"
			? "bg-indigo-500/20 text-indigo-200"
			: "bg-emerald-600/20 text-emerald-200";
	const ownerLabelFor = (ownerId) => {
		const match = adminOwners.find(
			(owner) => owner.value === String(ownerId || ""),
		);
		return match?.label || String(ownerId || "");
	};

	const loadTemplates = useCallback(
		async (scope, ownerId = "all") => {
			setLoading(true);
			try {
				const ownerQuery =
					canManageGlobal && scope === "all" && ownerId !== "all"
						? `&owner_id=${encodeURIComponent(ownerId)}`
						: "";
				const endpoint = `/conditioning-library?scope=${encodeURIComponent(scope)}${ownerQuery}`;
				const data = await fetchAPI(endpoint);
				setTemplates(
					applyClientScopeFilter(
						Array.isArray(data) ? data : [],
						scope,
						ownerId,
					),
				);
			} catch (error) {
				console.error("Error loading conditioning library:", error);
				toast.error("Errore nel caricamento conditioning library");
			} finally {
				setLoading(false);
			}
		},
		[canManageGlobal, currentUserId],
	);

	useEffect(() => {
		loadTemplates(scopeFilter, selectedOwnerId);
	}, [loadTemplates, scopeFilter, selectedOwnerId]);

	useEffect(() => {
		if (!canManageGlobal) return;
		const loadOwners = async () => {
			try {
				const payload = await fetchAPI("/admin/coaches-with-athletes");
				const owners = Array.isArray(payload)
					? payload
					: Array.isArray(payload?.coaches)
						? payload.coaches
						: [];
				const options = owners.map((coach) => ({
					value: coach.user_id,
					label: coach.name || coach.email || coach.user_id,
				}));
				setAdminOwners(options);
			} catch (error) {
				console.error("Error loading owners:", error);
			}
		};
		loadOwners();
	}, [canManageGlobal]);

	useEffect(() => {
		const loadEditorExercises = async () => {
			try {
				const data = await fetchAPI("/exercises?scope=visible");
				setEditorExercises(Array.isArray(data) ? data : []);
			} catch (error) {
				console.error("Error loading editor exercises:", error);
				setEditorExercises([]);
			}
		};
		loadEditorExercises();
	}, []);

	const filtered = useMemo(() => {
		return templates.filter((tpl) => {
			const search = searchTerm.trim().toLowerCase();
			const matchesSearch =
				!search ||
				tpl.name?.toLowerCase().includes(search) ||
				tpl.conditioning_type?.toLowerCase().includes(search);
			const matchesType =
				typeFilter === "all" || tpl.conditioning_type === typeFilter;
			return matchesSearch && matchesType;
		});
	}, [templates, searchTerm, typeFilter]);

	const openCreate = () => {
		setEditingTemplate(null);
		setFormData({
			...emptyConditioningTemplate(),
			library_scope: "personal",
		});
		setShowDialog(true);
	};

	const openEdit = (template) => {
		setEditingTemplate(template);
		setFormData({
			...emptyConditioningTemplate(),
			...template,
			library_scope: template?.library_scope || "personal",
		});
		setShowDialog(true);
	};

	const closeDialog = () => {
		setShowDialog(false);
		setEditingTemplate(null);
		setFormData({
			...emptyConditioningTemplate(),
			library_scope: "personal",
		});
	};

	// When the result-schema edit is blocked because results already exist, open a
	// visible dialog offering to convert the legacy free score into the new field.
	const [migration, setMigration] = useState(null); // { conditioningId, newSchema, dry }
	const [migrating, setMigrating] = useState(false);

	const runSchemaMigration = async (conditioningId, newSchema) => {
		try {
			const dry = await fetchAPI(
				`/conditioning-library/${conditioningId}/migrate-result-schema`,
				{ method: "POST", body: JSON.stringify({ result_schema: newSchema, dry_run: true }) },
			);
			if (!dry?.can_convert) {
				toast.error(t("schemaMigrateNeedsSingleField"));
				return;
			}
			// Close the edit modal so only the conversion dialog is visible.
			closeDialog();
			setMigration({ conditioningId, newSchema, dry });
		} catch (e) {
			toast.error(e?.message || t("errorSavingChanges"));
		}
	};

	const applyMigration = async () => {
		if (!migration) return;
		setMigrating(true);
		try {
			const res = await fetchAPI(
				`/conditioning-library/${migration.conditioningId}/migrate-result-schema`,
				{ method: "POST", body: JSON.stringify({ result_schema: migration.newSchema }) },
			);
			toast.success(
				t("schemaMigrateDone")
					.replace("{converted}", res.results_converted)
					.replace("{programs}", res.programs_updated),
			);
			setMigration(null);
			closeDialog();
			await loadTemplates(scopeFilter, selectedOwnerId);
		} catch (e) {
			toast.error(e?.message || t("errorSavingChanges"));
		} finally {
			setMigrating(false);
		}
	};

	const handleSave = async () => {
		if (!formData.name?.trim()) {
			toast.error(t("conditioningNameRequired"));
			return;
		}
		if (!formData.conditioning_type?.trim()) {
			toast.error(t("conditioningTypeRequired"));
			return;
		}

		setSaving(true);
		try {
			const payload = {
				...formData,
				rounds: normalizeConditioningRounds(formData.rounds),
			};
			if (!canManageGlobal) {
				payload.library_scope = "personal";
			}

			if (editingTemplate?.conditioning_id) {
				await fetchAPI(
					`/conditioning-library/${editingTemplate.conditioning_id}`,
					{
						method: "PUT",
						body: JSON.stringify(payload),
					},
				);
				toast.success(t("saveChanges"));
			} else {
				await fetchAPI("/conditioning-library", {
					method: "POST",
					body: JSON.stringify(payload),
				});
				toast.success(t("success"));
			}

			closeDialog();
			await loadTemplates(scopeFilter, selectedOwnerId);
		} catch (error) {
			const msg = String(error?.detail || error?.message || "").toLowerCase();
			const isSchemaLock =
				error?.status === 409 ||
				msg.includes("schema risultato bloccato") ||
				msg.includes("risultati inseriti");
			if (editingTemplate?.conditioning_id && isSchemaLock) {
				await runSchemaMigration(
					editingTemplate.conditioning_id,
					formData.result_schema || [],
				);
			} else {
				console.error("Error saving template:", error);
				toast.error(error?.message || t("errorSavingChanges"));
			}
		} finally {
			setSaving(false);
		}
	};

	const handleDelete = async (conditioningId) => {
		if (!window.confirm(t("confirmDelete"))) {
			return;
		}
		try {
			await fetchAPI(`/conditioning-library/${conditioningId}`, {
				method: "DELETE",
			});
			toast.success(t("delete"));
			await loadTemplates(scopeFilter, selectedOwnerId);
		} catch (error) {
			console.error("Error deleting template:", error);
			toast.error(t("errorSavingChanges"));
		}
	};

	return (
		<Layout>
			<div className="max-w-7xl mx-auto px-4 md:px-6 py-6 md:py-8 space-y-6">
				<div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
					<div>
						<h1 className="text-2xl font-heading tracking-tight">
							{t("conditioningLibraryTitle")}
						</h1>
						<p className="text-sm text-muted-foreground">
							{t("conditioningLibrarySubtitle")}
						</p>
					</div>
					<Button
						onClick={openCreate}
						className="bg-volt hover:bg-volt/90 rounded-sm"
					>
						<Plus className="w-4 h-4 mr-2" />
						{t("newConditioningCircuit")}
					</Button>
				</div>

				<Card className="bg-card border-border rounded-sm">
					<CardHeader className="space-y-3">
						<CardTitle className="text-base">{t("filters")}</CardTitle>
						<div className="flex flex-col gap-3">
							<div className="flex flex-col sm:flex-row gap-3">
								<div className="relative flex-1">
									<Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
									<Input
										value={searchTerm}
										onChange={(e) => setSearchTerm(e.target.value)}
										placeholder={t("search")}
										className="pl-10 rounded-sm bg-secondary border-transparent h-12"
									/>
								</div>
								<div className="flex flex-wrap gap-2">
									<Button
										type="button"
										variant={scopeFilter === "mine" ? "default" : "outline"}
										onClick={() => setScopeFilter("mine")}
										className="rounded-sm"
									>
										{t("libraryScopePersonalPlural")}
									</Button>
									<Button
										type="button"
										variant={scopeFilter === "global" ? "default" : "outline"}
										onClick={() => setScopeFilter("global")}
										className="rounded-sm"
									>
										{t("libraryScopeGlobalPlural")}
									</Button>
									<Button
										type="button"
										variant={scopeFilter === "visible" ? "default" : "outline"}
										onClick={() => setScopeFilter("visible")}
										className="rounded-sm"
									>
										{t("libraryScopeVisible")}
									</Button>
									{canManageGlobal && (
										<Button
											type="button"
											variant={scopeFilter === "all" ? "default" : "outline"}
											onClick={() => setScopeFilter("all")}
											className="rounded-sm"
										>
											{t("libraryScopeAllAdmin")}
										</Button>
									)}
								</div>
								{canManageGlobal && scopeFilter === "all" && (
									<Select
										value={selectedOwnerId}
										onValueChange={setSelectedOwnerId}
									>
										<SelectTrigger className="bg-secondary border-transparent rounded-sm w-full sm:w-[260px]">
											<SelectValue placeholder={t("ownerFilterLabel")} />
										</SelectTrigger>
										<SelectContent className="bg-card border-border">
											<SelectItem value="all">{t("allOwners")}</SelectItem>
											{adminOwners.map((owner) => (
												<SelectItem key={owner.value} value={owner.value}>
													{owner.label}
												</SelectItem>
											))}
										</SelectContent>
									</Select>
								)}
							</div>
							<div className="flex flex-wrap gap-2">
								<Button
									type="button"
									variant={typeFilter === "all" ? "default" : "outline"}
									onClick={() => setTypeFilter("all")}
									className="rounded-sm"
								>
									{t("all")}
								</Button>
								{CONDITIONING_TYPES.map((ct) => (
									<Button
										key={ct.value}
										type="button"
										variant={typeFilter === ct.value ? "default" : "outline"}
										onClick={() => setTypeFilter(ct.value)}
										className="rounded-sm"
									>
										{ct.label}
									</Button>
								))}
							</div>
						</div>
					</CardHeader>
				</Card>

				{loading ? (
					<div className="flex items-center justify-center py-16">
						<div className="spinner"></div>
					</div>
				) : filtered.length === 0 ? (
					<Card className="bg-card border-border rounded-sm">
						<CardContent className="flex flex-col items-center justify-center py-16 text-center">
							<Zap className="w-10 h-10 text-muted-foreground mb-3" />
							<p className="font-heading text-lg mb-1">{t("noResults")}</p>
							<p className="text-sm text-muted-foreground mb-4">
								{t("createCircuit")}
							</p>
							<Button
								onClick={openCreate}
								className="bg-volt hover:bg-volt/90 rounded-sm"
							>
								<Plus className="w-4 h-4 mr-2" />
								{t("newConditioningCircuit")}
							</Button>
						</CardContent>
					</Card>
				) : (
					<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
						{filtered.map((tpl) => {
							const isGlobalReadOnly =
								tpl.library_scope === "global" && user?.role !== "admin";
							const typeLabel =
								CONDITIONING_TYPES.find(
									(ct) => ct.value === tpl.conditioning_type,
								)?.label || tpl.conditioning_type;
							const emomRounds = getDisplayEmomRounds(tpl);
							const intervalLabel = formatIntervalSecondsLabel(
								tpl.interval_seconds,
								"it",
							);
							const forTimeRoundsDisplay =
								tpl.conditioning_type === "for_time"
									? getForTimeRoundsDisplay(tpl.rounds)
									: "";

							return (
								<Card
									key={tpl.conditioning_id}
									className="bg-card border-border rounded-sm hover:border-volt/30"
								>
									<CardContent className="p-4 space-y-3">
										<div className="flex items-start justify-between gap-3">
											<div className="min-w-0">
												<p className="font-heading text-base truncate">
													{tpl.name}
												</p>
												<div className="mt-1 flex items-center gap-2 flex-wrap">
													<Badge
														className={`border-0 rounded-sm text-[10px] ${getConditioningTypeBadgeClass(
															tpl.conditioning_type,
														)}`}
													>
														{typeLabel}
													</Badge>
													<Badge
														className={`border-0 rounded-sm text-[10px] ${getScopeBadgeClass(
															tpl.library_scope,
														)}`}
													>
														{tpl.library_scope === "global"
															? t("libraryScopeGlobalSingle")
															: t("libraryScopePersonal")}
													</Badge>
													{canManageGlobal &&
														scopeFilter === "all" &&
														normalizeScope(tpl.library_scope) ===
															"personal" && (
															<Badge className="border-0 rounded-sm text-[10px] bg-slate-600/25 text-slate-200">
																{ownerLabelFor(tpl.owner_id || tpl.coach_id)}
															</Badge>
														)}
												</div>
											</div>
											<div className="flex gap-1">
												<button
													disabled={isGlobalReadOnly}
													title={
														isGlobalReadOnly
															? t("globalTemplateEditAdminOnly")
															: undefined
													}
													onClick={() => openEdit(tpl)}
													className="p-1.5 rounded-sm hover:bg-volt/10 text-muted-foreground hover:text-volt disabled:opacity-50"
												>
													<Edit2 className="w-3.5 h-3.5" />
												</button>
												<button
													disabled={isGlobalReadOnly}
													title={
														isGlobalReadOnly
															? t("globalTemplateDeleteAdminOnly")
															: undefined
													}
													onClick={() => handleDelete(tpl.conditioning_id)}
													className="p-1.5 rounded-sm hover:bg-blaze/10 text-muted-foreground hover:text-blaze disabled:opacity-50"
												>
													<Trash2 className="w-3.5 h-3.5" />
												</button>
											</div>
										</div>

										<div className="flex flex-wrap gap-2 text-[10px] text-muted-foreground">
											{tpl.time_cap_seconds && (
												<span className="bg-secondary px-2 py-0.5 rounded-sm flex items-center gap-1">
													<Timer className="w-3 h-3" />
													{Math.floor(tpl.time_cap_seconds / 60)}'
												</span>
											)}
											{tpl.rounds && (
												<span className="bg-secondary px-2 py-0.5 rounded-sm">
													{tpl.conditioning_type === "for_time"
														? forTimeRoundsDisplay
														: `${tpl.rounds} ${tpl.rounds === 1 ? "round" : "rounds"}`}
												</span>
											)}
											{tpl.conditioning_type === "emom" && intervalLabel && (
												<span className="bg-secondary px-2 py-0.5 rounded-sm">
													{intervalLabel}
												</span>
											)}
											{tpl.conditioning_type === "emom" && emomRounds && (
												<span className="bg-secondary px-2 py-0.5 rounded-sm">
													{emomRounds} {emomRounds === 1 ? "round" : "rounds"}
												</span>
											)}
										</div>
									</CardContent>
								</Card>
							);
						})}
					</div>
				)}

				<Dialog open={showDialog} onOpenChange={setShowDialog}>
					<DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto rounded-sm">
						<DialogHeader>
							<DialogTitle>
								{editingTemplate
									? t("editConditioningCircuit")
									: t("newConditioningCircuit")}
							</DialogTitle>
							<DialogDescription>
								{t("conditioningLibrarySubtitle")}
							</DialogDescription>
						</DialogHeader>

						<ConditioningTemplateEditor
							value={formData}
							setValue={setFormData}
							exercises={editorExercises}
							showNameField={true}
							isAdmin={canManageGlobal}
						/>

						<div className="flex justify-end gap-2 pt-2">
							<Button
								variant="outline"
								onClick={closeDialog}
								disabled={saving}
								className="rounded-sm"
							>
								{t("cancel")}
							</Button>
							<Button
								onClick={handleSave}
								disabled={saving}
								className="bg-volt hover:bg-volt/90 rounded-sm"
							>
								{saving ? t("saving") : t("save")}
							</Button>
						</div>
					</DialogContent>
				</Dialog>

				{/* Result-schema conversion (unlock) dialog */}
				<Dialog
					open={!!migration}
					onOpenChange={(o) => {
						if (!o && !migrating) setMigration(null);
					}}
				>
					<DialogContent className="bg-card border-border">
						<DialogHeader>
							<DialogTitle>{t("schemaMigrateTitle")}</DialogTitle>
							<DialogDescription>{t("schemaMigrateIntro")}</DialogDescription>
						</DialogHeader>
						{migration?.dry && (
							<div className="space-y-2 text-sm">
								<div className="flex justify-between gap-3">
									<span className="text-muted-foreground">
										{t("schemaMigrateResultsToConvert")}
									</span>
									<span className="font-semibold">
										{migration.dry.results_convertible} / {migration.dry.results_total}
									</span>
								</div>
								<div className="flex justify-between gap-3">
									<span className="text-muted-foreground">
										{t("schemaMigrateTargetField")}
									</span>
									<span className="font-semibold">{migration.dry.target_title}</span>
								</div>
								<div className="flex justify-between gap-3">
									<span className="text-muted-foreground">
										{t("schemaMigratePrograms")}
									</span>
									<span className="font-semibold">{migration.dry.programs_affected}</span>
								</div>
								<p className="text-xs text-muted-foreground pt-1">
									{t("schemaMigrateHint")}
								</p>
							</div>
						)}
						<div className="flex justify-end gap-2 pt-2">
							<Button
								variant="outline"
								onClick={() => setMigration(null)}
								disabled={migrating}
								className="rounded-sm"
							>
								{t("cancel")}
							</Button>
							<Button
								onClick={applyMigration}
								disabled={migrating}
								className="bg-volt text-black font-bold rounded-sm hover:bg-volt/90"
							>
								{migrating ? t("saving") : t("schemaMigrateConfirmBtn")}
							</Button>
						</div>
					</DialogContent>
				</Dialog>
			</div>
		</Layout>
	);
}
